import {
  TableCellTypes,
  CellAligns,
  Badge,
  BadgeSizes,
  BadgeVariants,
  TypographyWeights,
} from '@carboledger/ui';
import { CellText } from '@/components/common';
import { RECONCILIATION_STATUS_TONE, MOVEMENT_STATUS_TONE } from '@/config/statusTones';

// The three Summary reports' columns (Figma 4196:130371). Every quantity is in the unit shown.

const badgeCell = (tones, codeField, labelField) => {
  const renderBadge = (ctx) => {
    const label = ctx.row[labelField];
    // An unmapped code renders as a dash, never as its raw enum value.
    if (!label) return <CellText>-</CellText>;
    return (
      <Badge
        variant={tones[ctx.row[codeField]] ?? BadgeVariants.default}
        size={BadgeSizes.sm}
        withDot
      >
        {label}
      </Badge>
    );
  };
  return renderBadge;
};

const renderBold = (ctx) => <CellText weight={TypographyWeights.semibold}>{ctx.value}</CellText>;

const text = (id, header, width) => ({ id, header, type: TableCellTypes.text, width });
const figure = (id, header, width) => ({ id, header, type: TableCellTypes.number, width });
const serialNo = (t) => ({ ...text('serialNo', t('FIELD_SERIAL_NO'), 60), pinnable: true });

export const buildMassBalanceColumns = (t, { unit }) => [
  serialNo(t),
  text('rawMaterialType', t('FIELD_RAW_MATERIAL_TYPE'), 180),
  text('countryOfOrigin', t('FIELD_COUNTRY_OF_ORIGIN'), 150),
  text('ghgSavings', t('INVENTORY_COL_GHG_SAVINGS'), 120),
  text('ghgEmissions', t('INVENTORY_COL_GHG_EMISSIONS'), 150),
  figure('openingBalance', t('INVENTORY_COL_OPENING_BALANCE', { unit }), 150),
  figure('inboundQty', t('INVENTORY_COL_INBOUND_QUANTITY', { unit }), 150),
  figure('allocatedQty', t('INVENTORY_COL_ALLOCATED_QUANTITY', { unit }), 150),
  figure('closingBalance', t('INVENTORY_COL_CLOSING_BALANCE', { unit }), 150),
];

export const buildReconciliationColumns = (t, { unit }) => [
  serialNo(t),
  text('reconciliationPeriod', t('INVENTORY_COL_RECONCILIATION_PERIOD'), 200),
  figure('availableClosingBalance', t('INVENTORY_COL_AVAILABLE_CLOSING_BALANCE', { unit }), 170),
  figure('reconciledQuantity', t('INVENTORY_COL_RECONCILED_QUANTITY', { unit }), 150),
  figure('adjustedClosingBalance', t('INVENTORY_COL_ADJUSTED_CLOSING_BALANCE', { unit }), 170),
  figure('physicalClosingBalance', t('INVENTORY_COL_PHYSICAL_BALANCE', { unit }), 150),
  figure('variance', t('INVENTORY_COL_VARIANCE', { unit }), 140),
  {
    id: 'status',
    header: t('FIELD_STATUS'),
    type: TableCellTypes.custom,
    width: 150,
    cell: badgeCell(RECONCILIATION_STATUS_TONE, 'statusCode', 'status'),
  },
];

const movementStatus = (t) => ({
  id: 'status',
  header: t('FIELD_STATUS'),
  type: TableCellTypes.custom,
  width: 190,
  cell: badgeCell(MOVEMENT_STATUS_TONE, 'statusCode', 'status'),
});

const movementMaterial = (t) => [
  text('rawMaterialType', t('FIELD_RAW_MATERIAL_TYPE'), 160),
  text('countryOfOrigin', t('FIELD_COUNTRY_OF_ORIGIN'), 140),
  text('ghgSavings', t('FIELD_GHG_EMISSION_SAVINGS'), 160),
  text('ghgEmissions', t('FIELD_GHG_EMISSIONS'), 160),
];

// One column per period in the range, then the grand total in bold.
export const buildMovementSummaryColumns = (t, { periods }) => [
  serialNo(t),
  movementStatus(t),
  ...movementMaterial(t),
  ...periods.map((period) => figure(period.id, period.label, 180)),
  {
    id: 'grandTotal',
    header: t('INVENTORY_COL_GRAND_TOTAL'),
    type: TableCellTypes.custom,
    align: CellAligns.right,
    width: 160,
    cell: renderBold,
  },
];

export const buildMovementDetailedColumns = (t, { unit }) => [
  serialNo(t),
  movementStatus(t),
  text('referenceId', t('FIELD_REFERENCE_ID'), 140),
  text('declarationNo', t('FIELD_DECLARATION_NO'), 150),
  text('counterpartyName', t('INVENTORY_COL_SUPPLIER_CUSTOMER'), 170),
  ...movementMaterial(t),
  text('movementDate', t('INVENTORY_COL_ASSOCIATED_DATE'), 150),
  figure('quantityText', t('INVENTORY_COL_QUANTITY', { unit }), 160),
];
